import { useEffect, useState, useCallback, useRef } from "react";
import { usePortal } from "../PortalShell";
import { portalQueries, readPortalCache, writePortalCache } from "@/client/lib/portal";
import { useAuth } from "@/client/lib/auth";
import { Button } from "@/client/components/ui/button";
import { Notice } from "@/client/components/ui/notice";
import { Input } from "@/client/components/ui/input";
import { Label } from "@/client/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectPopup, SelectItem } from "@/client/components/ui/select";
import { AlertCircleIcon, TriangleAlert } from "lucide-react";

type Contact = {
  personId: number;
  contactRoleCode: string;
  schoolLocationId: number | null;
  notes: string | null;
  workDays: string[] | null;
  emergencyPhone: string | null;
  isPrimary: boolean;
  givenName: string | null;
  familyName: string | null;
  preferredName: string | null;
};
type Email = { personId: number; email: string; label: string | null; isPrimary: boolean };

type EmergencyContact = { givenName: string; familyName: string; phoneNumber: string };
const emptyEmergency: EmergencyContact = { givenName: "", familyName: "", phoneNumber: "" };

// Werkdagen of the kunstcoördinator (ma-vrij); codes match dayOfWeekCodes, stored on the contact.
const WERKDAGEN = [
  { code: "monday", label: "Ma" },
  { code: "tuesday", label: "Di" },
  { code: "wednesday", label: "Wo" },
  { code: "thursday", label: "Do" },
  { code: "friday", label: "Vr" },
] as const;

type WijkOption = { code: string; label: string };
type SchoolForm = { streetName: string; houseNumber: string; houseNumberSuffix: string; postalCode: string; city: string; wijkCode: string; phoneNumber: string };
const emptySchool: SchoolForm = { streetName: "", houseNumber: "", houseNumberSuffix: "", postalCode: "", city: "", wijkCode: "", phoneNumber: "" };

type DirecteurForm = { givenName: string; familyName: string; email: string };
const emptyDirecteur: DirecteurForm = { givenName: "", familyName: "", email: "" };

// Format a Dutch postcode (DDDD LL) as the user types: keep only digits + letters, cap at
// 4 digits and 2 letters, uppercase the letters, and insert the single space between them.
function formatPostalCode(raw: string): string {
  const cleaned = raw.replace(/[^0-9a-zA-Z]/g, "");
  let digits = "";
  let letters = "";
  for (const ch of cleaned) {
    if (digits.length < 4 && /\d/.test(ch)) digits += ch;
    else if (/[a-zA-Z]/.test(ch) && letters.length < 2) letters += ch.toUpperCase();
  }
  return letters ? `${digits} ${letters}` : digits;
}

function validateSchool(f: SchoolForm): Partial<Record<keyof SchoolForm, string>> {
  const e: Partial<Record<keyof SchoolForm, string>> = {};
  if (!f.streetName.trim()) e.streetName = "Vul de straatnaam in.";
  if (!f.houseNumber.trim()) e.houseNumber = "Vul het huisnummer in.";
  if (!/^\d{4}\s?[A-Za-z]{2}$/.test(f.postalCode.trim())) e.postalCode = "Bijvoorbeeld 3441 AB.";
  if (!f.city.trim()) e.city = "Vul de plaats in.";
  if (!f.wijkCode) e.wijkCode = "Kies een wijk.";
  if (!f.phoneNumber.trim()) e.phoneNumber = "Vul het telefoonnummer van de school in.";
  return e;
}

export function ContactpersonenSection({ schoolId }: { schoolId: number }) {
  const { primaryAction, setSaveState, setLastSavedAt, saveState } = usePortal();
  const { user } = useAuth();
  const myPersonId = user?.personId ?? null;


  // Seed from the prefetched cache so the first paint is instant (no skeleton); load()
  // still revalidates in the background and writes fresh data back to the cache.
  const cachedContacts = readPortalCache<{ contacts?: Contact[]; emails?: Email[] }>(portalQueries.contacts(schoolId).queryKey);
  const [contacts, setContacts] = useState<Contact[]>(() => cachedContacts?.contacts ?? []);
  const [emails, setEmails] = useState<Email[]>(() => cachedContacts?.emails ?? []);
  const [loading, setLoading] = useState(() => !cachedContacts);
  const [error, setError] = useState(false);

  // Schoolgegevens: bezoekadres + wijk (the school_info section also covers these).
  const [wijkOptions, setWijkOptions] = useState<WijkOption[]>([]);
  const [schoolForm, setSchoolForm] = useState<SchoolForm>(emptySchool);
  const savedSchoolRef = useRef<SchoolForm>(emptySchool);
  const [schoolErrors, setSchoolErrors] = useState<Partial<Record<keyof SchoolForm, string>>>({});

  // Noodcontactpersoon (school-level emergency contact, exactly one per school).
  const [emergency, setEmergency] = useState<EmergencyContact>(emptyEmergency);
  const savedEmergencyRef = useRef<EmergencyContact>(emptyEmergency);
  const [emergencyAttempted, setEmergencyAttempted] = useState(false);

  // Directeur: a single create-or-edit-inline person.
  const [directeur, setDirecteur] = useState<DirecteurForm>(emptyDirecteur);
  const savedDirecteurRef = useRef<DirecteurForm>(emptyDirecteur);
  const directeurPersonIdRef = useRef<number | null>(null);
  const [directeurErrors, setDirecteurErrors] = useState<Partial<Record<keyof DirecteurForm, string>>>({});

  const load = useCallback(async () => {
    try {
      setError(false);
      const [contactsRes, emergencyRes] = await Promise.all([
        fetch(`/api/school/${schoolId}/contacts`, { credentials: "include" }),
        fetch(`/api/school/${schoolId}/emergency-contact`, { credentials: "include" }),
      ]);
      if (contactsRes.ok) {
        const d = await contactsRes.json();
        setContacts(d.contacts ?? []);
        setEmails(d.emails ?? []);
        setWijkOptions(d.wijkOptions ?? []);
        const sf: SchoolForm = {
          streetName: d.school?.visitAddress?.streetName ?? "",
          houseNumber: d.school?.visitAddress?.houseNumber ?? "",
          houseNumberSuffix: d.school?.visitAddress?.houseNumberSuffix ?? "",
          postalCode: d.school?.visitAddress?.postalCode ?? "",
          city: d.school?.visitAddress?.city ?? "",
          wijkCode: d.school?.wijkCode ?? "",
          phoneNumber: d.school?.phoneNumber ?? "",
        };
        setSchoolForm(sf);
        savedSchoolRef.current = sf;

        // Seed the directeur form from the (single) directeur contact, if any.
        const dir = (d.contacts as Contact[] | undefined)?.find((c) => c.contactRoleCode === "directeur") ?? null;
        const dirEmail = dir
          ? (d.emails as Email[] | undefined)?.find((e) => e.personId === dir.personId && e.isPrimary)?.email
            ?? (d.emails as Email[] | undefined)?.find((e) => e.personId === dir.personId)?.email
            ?? ""
          : "";
        const df: DirecteurForm = {
          givenName: dir?.givenName ?? "",
          familyName: dir?.familyName ?? "",
          email: dirEmail,
        };
        setDirecteur(df);
        savedDirecteurRef.current = df;
        directeurPersonIdRef.current = dir?.personId ?? null;

        writePortalCache(portalQueries.contacts(schoolId).queryKey, d);
      } else {
        setError(true);
      }
      if (emergencyRes.ok) {
        const d = await emergencyRes.json();
        const ec: EmergencyContact = {
          givenName: d.emergencyContact?.givenName ?? "",
          familyName: d.emergencyContact?.familyName ?? "",
          phoneNumber: d.emergencyContact?.phoneNumber ?? "",
        };
        setEmergency(ec);
        savedEmergencyRef.current = ec;
      }
    } catch {
      setError(true);
    }
    setLoading(false);
  }, [schoolId]);

  useEffect(() => { load(); }, [load]);

  const saved = useCallback(() => {
    setSaveState("saved");
    setLastSavedAt(new Date().toLocaleTimeString("nl-NL", { hour: "2-digit", minute: "2-digit" }));
  }, [setSaveState, setLastSavedAt]);

  const getEmail = (id: number) => emails.find((e) => e.personId === id && e.isPrimary)?.email ?? emails.find((e) => e.personId === id)?.email ?? "";

  const isMe = (c: Contact) => myPersonId !== null && c.personId === myPersonId;

  // Coordinators (KUVO-managed), in a stable creation-order by personId. Naam + e-mail are
  // read-only here; the school edits only the werkdagen, which autosave per coordinator.
  const coordinators = contacts
    .filter((c) => c.contactRoleCode === "cultuurcoordinator")
    .slice()
    .sort((a, b) => a.personId - b.personId);

  // ── Completeness (mirrors the server rule in info.ts /progress) ──
  const schoolComplete = Object.keys(validateSchool(savedSchoolRef.current)).length === 0;
  const coordinatorsComplete = coordinators.every((c) => (c.workDays?.length ?? 0) > 0);
  const emergencyComplete = !!(
    savedEmergencyRef.current.givenName.trim() &&
    savedEmergencyRef.current.familyName.trim() &&
    savedEmergencyRef.current.phoneNumber.trim()
  );
  const hasDirecteur = contacts.some((c) => c.contactRoleCode === "directeur");

  // Reveal the per-coordinator werkdagen validation once the school tries to continue.
  const [coordinatorSubmitted, setCoordinatorSubmitted] = useState(false);

  // ── School address autosave (debounced + in-flight guarded) ──
  // The school_info endpoint requires the WHOLE form, so we only persist once every field is
  // valid; otherwise we just surface the inline validation and wait.
  const schoolTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const schoolInFlight = useRef(false);
  const setSchool = (key: keyof SchoolForm, val: string) => {
    setSchoolForm((f) => {
      const next = { ...f, [key]: val };
      scheduleSchoolSave(next);
      return next;
    });
  };
  const scheduleSchoolSave = (next: SchoolForm) => {
    if (schoolTimer.current) clearTimeout(schoolTimer.current);
    schoolTimer.current = setTimeout(() => saveSchool(next), 700);
  };
  const saveSchool = useCallback(async (next: SchoolForm) => {
    const errs = validateSchool(next);
    setSchoolErrors(errs);
    if (Object.keys(errs).length > 0) return;
    // Nothing changed since the last save? Skip the round-trip.
    if (JSON.stringify(next) === JSON.stringify(savedSchoolRef.current)) return;
    if (schoolInFlight.current) {
      // A save is already running; reschedule so the latest value still lands.
      scheduleSchoolSave(next);
      return;
    }
    schoolInFlight.current = true;
    setSaveState("saving");
    try {
      const res = await fetch(`/api/school/${schoolId}/school-info`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          streetName: next.streetName.trim(),
          houseNumber: next.houseNumber.trim(),
          houseNumberSuffix: next.houseNumberSuffix.trim() || undefined,
          postalCode: next.postalCode.trim(),
          city: next.city.trim(),
          wijkCode: next.wijkCode,
          phoneNumber: next.phoneNumber.trim(),
        }),
      });
      if (res.ok) {
        savedSchoolRef.current = next;
        setSchoolErrors({});
        saved();
      } else {
        setSaveState("error");
      }
    } catch {
      setSaveState("error");
    }
    schoolInFlight.current = false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schoolId, saved, setSaveState]);
  const flushSchool = (next: SchoolForm) => {
    if (schoolTimer.current) clearTimeout(schoolTimer.current);
    saveSchool(next);
  };

  // ── Noodcontactpersoon autosave (debounced + in-flight guarded) ──
  const emergencyMissing = {
    givenName: !emergency.givenName.trim(),
    familyName: !emergency.familyName.trim(),
    phoneNumber: !emergency.phoneNumber.trim(),
  };
  // Reveal the noodcontactpersoon validation after a save attempt or when blocking Doorgaan.
  const showEmergencyErrors = emergencyAttempted || (coordinatorSubmitted && !emergencyComplete);
  const emergencyTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const emergencyInFlight = useRef(false);
  const setEmergencyField = (key: keyof EmergencyContact, val: string) => {
    setEmergency((f) => {
      const next = { ...f, [key]: val };
      scheduleEmergencySave(next);
      return next;
    });
  };
  const scheduleEmergencySave = (next: EmergencyContact) => {
    if (emergencyTimer.current) clearTimeout(emergencyTimer.current);
    emergencyTimer.current = setTimeout(() => saveEmergency(next), 700);
  };
  const saveEmergency = useCallback(async (next: EmergencyContact) => {
    // All three fields are required server-side; only persist a complete contact.
    if (!next.givenName.trim() || !next.familyName.trim() || !next.phoneNumber.trim()) return;
    if (JSON.stringify(next) === JSON.stringify(savedEmergencyRef.current)) return;
    if (emergencyInFlight.current) {
      scheduleEmergencySave(next);
      return;
    }
    emergencyInFlight.current = true;
    setSaveState("saving");
    try {
      const res = await fetch(`/api/school/${schoolId}/emergency-contact`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          givenName: next.givenName.trim(),
          familyName: next.familyName.trim(),
          phoneNumber: next.phoneNumber.trim(),
        }),
      });
      if (res.ok) {
        savedEmergencyRef.current = next;
        saved();
      } else {
        setSaveState("error");
      }
    } catch {
      setSaveState("error");
    }
    emergencyInFlight.current = false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schoolId, saved, setSaveState]);
  const flushEmergency = (next: EmergencyContact) => {
    if (emergencyTimer.current) clearTimeout(emergencyTimer.current);
    saveEmergency(next);
  };

  // ── Directeur autosave (debounced + in-flight guarded) — create-or-edit one directeur ──
  const directeurTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const directeurInFlight = useRef(false);
  const validateDirecteur = (f: DirecteurForm): Partial<Record<keyof DirecteurForm, string>> => {
    const e: Partial<Record<keyof DirecteurForm, string>> = {};
    if (!f.givenName.trim()) e.givenName = "Vul een voornaam in.";
    if (!f.familyName.trim()) e.familyName = "Vul een achternaam in.";
    if (!f.email.trim()) e.email = "Vul een e-mailadres in.";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(f.email.trim())) e.email = "Ongeldig e-mailadres.";
    return e;
  };
  const setDirecteurField = (key: keyof DirecteurForm, val: string) => {
    setDirecteur((f) => {
      const next = { ...f, [key]: val };
      scheduleDirecteurSave(next);
      return next;
    });
  };
  const scheduleDirecteurSave = (next: DirecteurForm) => {
    if (directeurTimer.current) clearTimeout(directeurTimer.current);
    directeurTimer.current = setTimeout(() => saveDirecteur(next), 700);
  };
  const saveDirecteur = useCallback(async (next: DirecteurForm) => {
    const errs = validateDirecteur(next);
    setDirecteurErrors(errs);
    if (Object.keys(errs).length > 0) return;
    if (JSON.stringify(next) === JSON.stringify(savedDirecteurRef.current)) return;
    if (directeurInFlight.current) {
      scheduleDirecteurSave(next);
      return;
    }
    directeurInFlight.current = true;
    setSaveState("saving");
    try {
      const existingId = directeurPersonIdRef.current;
      const res = existingId
        ? await fetch(`/api/school/${schoolId}/contacts/${existingId}`, {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            credentials: "include",
            body: JSON.stringify({
              givenName: next.givenName.trim(),
              familyName: next.familyName.trim(),
              email: next.email.trim(),
              contactRoleCode: "directeur",
              originalRoleCode: "directeur",
            }),
          })
        : await fetch(`/api/school/${schoolId}/contacts/create`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            credentials: "include",
            body: JSON.stringify({
              givenName: next.givenName.trim(),
              familyName: next.familyName.trim(),
              email: next.email.trim(),
              contactRoleCode: "directeur",
            }),
          });
      if (res.ok) {
        // On a create, capture the new personId so subsequent edits update in place. Then
        // reload so contacts/hasDirecteur/the cache reflect the new directeur.
        if (!existingId) {
          const body = await res.json().catch(() => ({}));
          if ((body as any).personId) directeurPersonIdRef.current = (body as any).personId;
        }
        savedDirecteurRef.current = next;
        setDirecteurErrors({});
        await load();
        saved();
      } else {
        setSaveState("error");
      }
    } catch {
      setSaveState("error");
    }
    directeurInFlight.current = false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schoolId, saved, setSaveState, load]);
  const flushDirecteur = (next: DirecteurForm) => {
    if (directeurTimer.current) clearTimeout(directeurTimer.current);
    saveDirecteur(next);
  };

  if (loading) return <p className="text-sm text-muted-foreground">Laden...</p>;

  if (error) {
    return (
      <Notice
        tone="error"
        icon={<AlertCircleIcon />}
        title="Kan de contactpersonen niet laden"
        action={
          <Button size="sm" variant="outline" onClick={() => { setLoading(true); load(); }}>
            Opnieuw proberen
          </Button>
        }
      >
        Controleer uw internetverbinding en probeer het opnieuw.
      </Notice>
    );
  }

  return (
    <div>
      <p className="text-sm leading-6 text-muted-foreground">
        Controleer het schooladres en de contactpersonen. Wat u hier aanpast, wordt automatisch opgeslagen.
      </p>

      {/* ── Bezoekadres en wijk ── */}
      <div className="mt-6 rounded-lg border bg-card p-4 sm:p-5">
        <h2 className="text-base font-semibold text-foreground">Bezoekadres en wijk</h2>
        <p className="mt-0.5 text-sm leading-6 text-muted-foreground">Het bezoekadres en de wijk van uw school.</p>
        <div className="mt-3 space-y-3">
          {/* Row 1: Straatnaam, Huisnummer, Toevoeging, Postcode. Proportional flex widths with a
              min-width per field, so they wrap onto more lines on narrow screens. */}
          <div className="flex flex-wrap gap-3">
            <div className="min-w-[12rem] flex-[4]">
              <Label className="text-xs">Straatnaam <span className="text-red-600">*</span></Label>
              <Input value={schoolForm.streetName} onChange={(e) => setSchool("streetName", e.target.value)} onBlur={() => flushSchool(schoolForm)} placeholder="Straatnaam" className="mt-1" />
              {schoolErrors.streetName && <p className="mt-1 text-xs text-red-600">{schoolErrors.streetName}</p>}
            </div>
            <div className="min-w-[5rem] flex-[1]">
              <Label className="text-xs">Huisnummer <span className="text-red-600">*</span></Label>
              <Input value={schoolForm.houseNumber} onChange={(e) => setSchool("houseNumber", e.target.value)} onBlur={() => flushSchool(schoolForm)} placeholder="12" className="mt-1" />
              {schoolErrors.houseNumber && <p className="mt-1 text-xs text-red-600">{schoolErrors.houseNumber}</p>}
            </div>
            <div className="min-w-[5rem] flex-[1]">
              <Label className="text-xs">Toevoeging</Label>
              <Input value={schoolForm.houseNumberSuffix} onChange={(e) => setSchool("houseNumberSuffix", e.target.value)} onBlur={() => flushSchool(schoolForm)} placeholder="A" className="mt-1" />
            </div>
            <div className="min-w-[8rem] flex-[2]">
              <Label className="text-xs">Postcode <span className="text-red-600">*</span></Label>
              <Input value={schoolForm.postalCode} onChange={(e) => setSchool("postalCode", formatPostalCode(e.target.value))} onBlur={() => flushSchool(schoolForm)} placeholder="3441 AB" inputMode="text" autoCapitalize="characters" maxLength={7} className="mt-1" />
              {schoolErrors.postalCode && <p className="mt-1 text-xs text-red-600">{schoolErrors.postalCode}</p>}
            </div>
          </div>
          {/* Row 2: Plaats, Wijk, Telefoonnummer. */}
          <div className="flex flex-wrap gap-3">
            <div className="min-w-[10rem] flex-1">
              <Label className="text-xs">Plaats <span className="text-red-600">*</span></Label>
              <Input value={schoolForm.city} onChange={(e) => setSchool("city", e.target.value)} onBlur={() => flushSchool(schoolForm)} placeholder="Woerden" className="mt-1" />
              {schoolErrors.city && <p className="mt-1 text-xs text-red-600">{schoolErrors.city}</p>}
            </div>
            <div className="min-w-[10rem] flex-1">
              <Label className="text-xs">Wijk <span className="text-red-600">*</span></Label>
              <Select value={schoolForm.wijkCode || null} onValueChange={(val) => { const wc = (val as string) ?? ""; setSchoolForm((f) => { const next = { ...f, wijkCode: wc }; flushSchool(next); return next; }); }}>
                <SelectTrigger className="mt-1">
                  {schoolForm.wijkCode
                    ? <span className="flex-1 truncate">{wijkOptions.find((w) => w.code === schoolForm.wijkCode)?.label ?? schoolForm.wijkCode}</span>
                    : <SelectValue placeholder="Kies een wijk" />}
                </SelectTrigger>
                <SelectPopup>
                  {wijkOptions.map((w) => <SelectItem key={w.code} value={w.code}>{w.label}</SelectItem>)}
                </SelectPopup>
              </Select>
              {schoolErrors.wijkCode && <p className="mt-1 text-xs text-red-600">{schoolErrors.wijkCode}</p>}
            </div>
            <div className="min-w-[10rem] flex-1">
              <Label className="text-xs">Telefoonnummer school <span className="text-red-600">*</span></Label>
              <Input type="tel" value={schoolForm.phoneNumber} onChange={(e) => setSchool("phoneNumber", e.target.value)} onBlur={() => flushSchool(schoolForm)} placeholder="0348-123456" className="mt-1" />
              {schoolErrors.phoneNumber && <p className="mt-1 text-xs text-red-600">{schoolErrors.phoneNumber}</p>}
            </div>
          </div>
        </div>
      </div>

      {/* ── Kunstcoördinatoren ── */}
      <div className="mt-8">
        <h2 className="text-base font-semibold text-foreground">Kunstcoördinatoren</h2>

        {/* Contactpersoon noodgevallen (school-level emergency contact) */}
        <div className="mt-4 rounded-lg border bg-card p-4 sm:p-5">
          <h3 className="text-sm font-semibold text-foreground">Contactpersoon noodgevallen <span className="text-red-600">*</span></h3>
          <p className="mt-0.5 text-sm leading-6 text-muted-foreground">
            Wie kan KUVO bellen als er iets onverwachts gebeurt rond een activiteit? We gebruiken dit alleen bij noodgevallen.
          </p>
          <div className="mt-3 space-y-3">
            {/* Voornaam, Achternaam, Telefoonnummer on one line; wraps on narrow screens. */}
            <div className="flex flex-wrap gap-3">
              <div className="min-w-[10rem] flex-1">
                <Label className="text-xs">Voornaam <span className="text-red-600">*</span></Label>
                <Input
                  value={emergency.givenName}
                  onChange={(e) => setEmergencyField("givenName", e.target.value)}
                  onBlur={() => flushEmergency(emergency)}
                  placeholder="Voornaam"
                  className="mt-1"
                  aria-invalid={showEmergencyErrors && emergencyMissing.givenName}
                />
                {showEmergencyErrors && emergencyMissing.givenName && <p className="mt-1 text-xs text-amber-700">Vul een voornaam in.</p>}
              </div>
              <div className="min-w-[10rem] flex-1">
                <Label className="text-xs">Achternaam <span className="text-red-600">*</span></Label>
                <Input
                  value={emergency.familyName}
                  onChange={(e) => setEmergencyField("familyName", e.target.value)}
                  onBlur={() => flushEmergency(emergency)}
                  placeholder="Achternaam"
                  className="mt-1"
                  aria-invalid={showEmergencyErrors && emergencyMissing.familyName}
                />
                {showEmergencyErrors && emergencyMissing.familyName && <p className="mt-1 text-xs text-amber-700">Vul een achternaam in.</p>}
              </div>
              <div className="order-first min-w-[10rem] flex-1">
                <Label className="text-xs">Telefoonnummer <span className="text-red-600">*</span></Label>
                <Input
                  type="tel"
                  value={emergency.phoneNumber}
                  onChange={(e) => setEmergencyField("phoneNumber", e.target.value)}
                  onBlur={() => flushEmergency(emergency)}
                  placeholder="06-12345678"
                  className="mt-1"
                  aria-invalid={showEmergencyErrors && emergencyMissing.phoneNumber}
                />
                {showEmergencyErrors && emergencyMissing.phoneNumber && <p className="mt-1 text-xs text-amber-700">Vul een telefoonnummer in.</p>}
              </div>
            </div>
          </div>
        </div>

        {/* Per-coordinator blocks (KUVO-managed identity + editable werkdagen) */}
        {coordinators.length === 0 ? (
          <div className="mt-5">
            <Notice tone="info" icon={<AlertCircleIcon />}>
              KUVO stelt de kunstcoördinator van uw school in. Klopt alles? Ontbreekt er iemand?
              Neem dan even contact op met KUVO.
            </Notice>
          </div>
        ) : (
          coordinators.map((c, i) => (
            <CoordinatorBlock
              key={c.personId}
              schoolId={schoolId}
              index={coordinators.length > 1 ? i + 1 : null}
              contact={c}
              email={getEmail(c.personId)}
              isMe={isMe(c)}
              submitted={coordinatorSubmitted}
              onValidationFail={() => setCoordinatorSubmitted(true)}
              onSaved={async () => { await load(); saved(); }}
              onError={() => setSaveState("error")}
              onSaving={() => setSaveState("saving")}
            />
          ))
        )}
      </div>

      {/* ── Directeur ── */}
      <div className="mt-8 rounded-lg border bg-card p-4 sm:p-5">
        <h2 className="text-base font-semibold text-foreground">Directeur</h2>
        <p className="mt-0.5 text-sm leading-6 text-muted-foreground">
          De contactgegevens van de directeur van de school.
        </p>
        <div className="mt-3 space-y-3">
          {/* Voornaam, Achternaam, E-mailadres on one line; wraps on narrow screens. */}
          <div className="flex flex-wrap gap-3">
            <div className="min-w-[9rem] flex-1">
              <Label className="text-xs">Voornaam <span className="text-red-600">*</span></Label>
              <Input
                value={directeur.givenName}
                onChange={(e) => setDirecteurField("givenName", e.target.value)}
                onBlur={() => flushDirecteur(directeur)}
                placeholder="Voornaam"
                className="mt-1"
              />
              {directeurErrors.givenName && <p className="mt-1 text-xs text-red-600">{directeurErrors.givenName}</p>}
            </div>
            <div className="min-w-[9rem] flex-1">
              <Label className="text-xs">Achternaam <span className="text-red-600">*</span></Label>
              <Input
                value={directeur.familyName}
                onChange={(e) => setDirecteurField("familyName", e.target.value)}
                onBlur={() => flushDirecteur(directeur)}
                placeholder="Achternaam"
                className="mt-1"
              />
              {directeurErrors.familyName && <p className="mt-1 text-xs text-red-600">{directeurErrors.familyName}</p>}
            </div>
            <div className="order-first min-w-[14rem] flex-[2]">
              <Label className="text-xs">E-mailadres <span className="text-red-600">*</span></Label>
              <Input
                type="email"
                value={directeur.email}
                onChange={(e) => setDirecteurField("email", e.target.value)}
                onBlur={() => flushDirecteur(directeur)}
                placeholder="naam@school.nl"
                className="mt-1"
              />
              {directeurErrors.email && <p className="mt-1 text-xs text-red-600">{directeurErrors.email}</p>}
            </div>
          </div>
        </div>
      </div>

      {/* Continue button, with any blocking reasons shown just above it as a clean inline warn-alert. */}
      <div className="mt-8">
        {primaryAction.isConfirm && coordinators.length > 0 && !coordinatorsComplete && (
          <div className="mb-3 rounded-md border px-4 py-3">
            <p className="text-sm">
              <TriangleAlert aria-hidden className="-mt-0.5 me-3 inline-flex text-amber-500" size={16} />
              Vul bij elke kunstcoördinator de werkdagen in om door te gaan.
            </p>
          </div>
        )}
        {primaryAction.isConfirm && !emergencyComplete && (
          <div className="mb-3 rounded-md border px-4 py-3">
            <p className="text-sm">
              <TriangleAlert aria-hidden className="-mt-0.5 me-3 inline-flex text-amber-500" size={16} />
              Vul de contactpersoon voor noodgevallen volledig in om door te gaan.
            </p>
          </div>
        )}
        <Button
          size="lg"
          onClick={() => {
            if (!emergencyComplete) setEmergencyAttempted(true);
            if (coordinators.length > 0 && !coordinatorsComplete) setCoordinatorSubmitted(true);
            primaryAction.onClick();
          }}
          disabled={primaryAction.isConfirm && (saveState === "saving" || !hasDirecteur || !schoolComplete || !coordinatorsComplete || !emergencyComplete)}
        >
          {primaryAction.label}
        </Button>
      </div>
    </div>
  );
}

// ─── Coordinator block ───
// KUVO owns the roster + identity: voornaam, achternaam and e-mail are read-only here (a wrong
// e-mail would break the coordinator's magic-link login). The SCHOOL fills in the werkdagen,
// which autosave on change.

function CoordinatorBlock({
  schoolId,
  index,
  contact,
  email,
  isMe,
  submitted,
  onValidationFail,
  onSaved,
  onError,
  onSaving,
}: {
  schoolId: number;
  // Coordinator number when there is more than one (1, 2, ...); null when there is only one.
  index: number | null;
  contact: Contact;
  email: string;
  isMe: boolean;
  // Reveal the werkdagen validation once the school has attempted to continue.
  submitted: boolean;
  onValidationFail: () => void;
  onSaved: () => Promise<void> | void;
  onError: () => void;
  onSaving: () => void;
}) {
  const heading = index ? `Kunstcoördinator ${index}` : "Kunstcoördinator 1";
  // The visible label drops the number when this is the school's only coordinator ("Kunstcoördinator 1"
  // reads oddly when there's just one). `heading` above is left untouched: it is only reused as the
  // server-ignored givenName fallback in the autosave payload below, so the display change is purely visual.
  const displayHeading = index ? `Kunstcoördinator ${index}` : "Kunstcoördinator";

  // The school-owned, autosaved field. Seeded from the saved contact and re-seeded whenever the
  // underlying data changes (after a reload).
  const [workDays, setWorkDays] = useState<string[]>(contact.workDays ?? []);
  const savedDaysRef = useRef<string[]>(contact.workDays ?? []);
  const inFlight = useRef(false);
  const pending = useRef<string[] | null>(null);

  useEffect(() => {
    setWorkDays(contact.workDays ?? []);
    savedDaysRef.current = contact.workDays ?? [];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contact.personId, JSON.stringify(contact.workDays ?? [])]);

  const workDaysMissing = workDays.length === 0;
  const showErrors = submitted;

  const persist = async (days: string[]) => {
    if (inFlight.current) { pending.current = days; return; }
    inFlight.current = true;
    onSaving();
    try {
      const res = await fetch(`/api/school/${schoolId}/contacts/${contact.personId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          // Name + e-mail are KUVO-managed and ignored server-side for a coordinator; we resend
          // the current values purely to satisfy the shared schema's required fields.
          givenName: contact.givenName ?? heading,
          familyName: contact.familyName ?? "",
          contactRoleCode: "cultuurcoordinator",
          originalRoleCode: "cultuurcoordinator",
          workDays: days,
        }),
      });
      if (res.ok) {
        savedDaysRef.current = days;
        await onSaved();
      } else {
        onError();
      }
    } catch {
      onError();
    }
    inFlight.current = false;
    if (pending.current) {
      const next = pending.current;
      pending.current = null;
      void persist(next);
    }
  };

  const toggleDay = (code: string) => {
    setWorkDays((prev) => {
      const next = prev.includes(code) ? prev.filter((x) => x !== code) : [...prev, code];
      if (next.length === 0) onValidationFail();
      else void persist(next);
      return next;
    });
  };

  return (
    <div className="mt-5 rounded-lg border bg-card p-4 sm:p-5">
      <h3 className="text-sm font-semibold text-foreground">{displayHeading}</h3>
      <div className="mt-3 space-y-3">
        {/* Voornaam, Achternaam, E-mailadres on one line; wraps on narrow screens. */}
        <div className="flex flex-wrap gap-3">
          <div className="min-w-[9rem] flex-1">
            <Label className="text-xs">Voornaam</Label>
            <Input value={contact.givenName ?? ""} disabled className="mt-1" />
          </div>
          <div className="min-w-[9rem] flex-1">
            <Label className="text-xs">Achternaam</Label>
            <Input value={contact.familyName ?? ""} disabled className="mt-1" />
          </div>
          <div className="order-first min-w-[14rem] flex-[2]">
            <Label className="text-xs">E-mailadres</Label>
            <Input type="email" value={email} disabled className="mt-1" />
            <p className="mt-1 text-xs text-muted-foreground">
              {isMe
                ? "Met dit e-mailadres bent u ingelogd. Neem contact op met KUVO Woerden als naam of e-mail moet worden gewijzigd."
                : "Neem contact op met KUVO Woerden als naam of e-mail moet worden gewijzigd."}
            </p>
          </div>
        </div>
        <div>
          <Label className="text-xs">Werkdagen <span className="text-red-600">*</span></Label>
          <p className="mt-0.5 text-xs text-muted-foreground">Op welke dagen is deze kunstcoördinator op school te bereiken?</p>
          <div className="mt-1.5 flex flex-wrap gap-2" data-invalid={showErrors && workDaysMissing ? "true" : undefined}>
            {WERKDAGEN.map((d) => {
              const on = workDays.includes(d.code);
              return (
                <button
                  key={d.code}
                  type="button"
                  onClick={() => toggleDay(d.code)}
                  className={`cursor-pointer rounded-md border px-3 py-1 text-xs font-medium transition-colors ${on ? "border-primary bg-primary/10 text-primary" : "border-input bg-background text-foreground hover:border-primary/40"}`}
                >
                  {d.label}
                </button>
              );
            })}
          </div>
          {showErrors && workDaysMissing && (
            <p className="mt-1 text-xs text-amber-700">Kies minstens één werkdag.</p>
          )}
        </div>
      </div>
    </div>
  );
}
